import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Truck, X, ChevronDown, ChevronUp, Plus, MapPin, GitMerge, AlertTriangle, Settings, Package, Info, ExternalLink, TestTube,
} from 'lucide-react';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import GooglePlacesSearch from '@/components/shared/GooglePlacesSearch';
import { useOrgaConfigurationContext } from '@/app/providers';
import { Card, CardHeader, CardBody, Button } from '@/components/ui';
import { TailwindDialog, DialogContent, DialogActions } from '@/components/ui/TailwindDialog';
import CarrierFinder from './CarrierFinder';
import type { ShippingMethodConfig, ShippingConfiguration } from '@/models/orgaConfiguration';
import type { SendCloudShippingMethod } from '@/models/sendcloud';
import { ShippingService } from '@/services/shipping/shipping-service';
import { isSendcloudPlatformProviderEnabled } from '@/shared/config/featureFlags';
import {
  applySendcloudMarkup,
  formatSendcloudWeightKg,
  sendcloudCountryCode,
} from '@/shared/utils/sendcloud-shipping';

const COUNTRY_NAMES: Record<string, string> = {
  FR: 'France', BE: 'Belgique', CH: 'Suisse', DE: 'Allemagne', ES: 'Espagne',
  IT: 'Italie', NL: 'Pays-Bas', GB: 'Royaume-Uni', PT: 'Portugal', AT: 'Autriche',
  LU: 'Luxembourg', IE: 'Irlande', DK: 'Danemark', SE: 'Suède', NO: 'Norvège',
  FI: 'Finlande', PL: 'Pologne', CZ: 'République tchèque', HU: 'Hongrie', SK: 'Slovaquie',
  SI: 'Slovénie', HR: 'Croatie', RO: 'Roumanie', BG: 'Bulgarie', GR: 'Grèce',
};
const COUNTRY_LIST = Object.entries(COUNTRY_NAMES).map(([code, name]) => ({ code, name }));

/** Mondial Relay — point relais / consigne (offre documentée « SAFE »). */
const MONDIAL_RELAY_RELAY_COUNTRY_CODES: readonly string[] = ['FR', 'BE', 'ES', 'PT', 'NL', 'LU'];
/** Mondial Relay — domicile (offre documentée « SAFE », + IT, DE, AT). */
const MONDIAL_RELAY_HOME_COUNTRY_CODES: readonly string[] = [
  'FR', 'BE', 'ES', 'PT', 'NL', 'LU', 'IT', 'DE', 'AT',
];

function isMondialRelayHomeProfile(
  method: Pick<ShippingMethodConfig, 'type' | 'mondialRelayModeLiv'>,
): boolean {
  return method.type === 'home' || method.mondialRelayModeLiv === '24L';
}

/** Codes pays autorisés pour une méthode MR (selon relais vs domicile). Locker = même périmètre que relais. */
function getMondialRelayCountryCodes(
  method: Pick<ShippingMethodConfig, 'provider' | 'type' | 'mondialRelayModeLiv'>,
): string[] {
  if (method.provider !== 'mondial_relay') return [];
  const codes = isMondialRelayHomeProfile(method)
    ? MONDIAL_RELAY_HOME_COUNTRY_CODES
    : MONDIAL_RELAY_RELAY_COUNTRY_CODES;
  return [...codes];
}

function getMondialRelayCountryRows(
  method: Pick<ShippingMethodConfig, 'provider' | 'type' | 'mondialRelayModeLiv'>,
): { code: string; name: string }[] {
  return getMondialRelayCountryCodes(method).map(code => ({
    code,
    name: COUNTRY_NAMES[code] ?? code,
  }));
}

/** Formulaire d’ajout MR (`relay` | `home` uniquement). */
function getMondialRelayCountryRowsForNewMethod(type: 'relay' | 'home'): { code: string; name: string }[] {
  return getMondialRelayCountryRows({
    provider: 'mondial_relay',
    type: type === 'home' ? 'home' : 'relay',
    mondialRelayModeLiv: type === 'home' ? '24L' : '24R',
  });
}

/** IDs stables Firestore pour les deux offres MR direct (remplace les anciens id aléatoires au prochain enregistrement). */
const MR_DIRECT_HOME_ID = 'mondial_relay_direct_home';
const MR_DIRECT_RELAY_ID = 'mondial_relay_direct_relay';

function findMrDirectMethod(
  methods: ShippingMethodConfig[],
  kind: 'home' | 'relay',
): ShippingMethodConfig | undefined {
  const wantId = kind === 'home' ? MR_DIRECT_HOME_ID : MR_DIRECT_RELAY_ID;
  const byId = methods.find(m => m.id === wantId);
  if (byId) return byId;
  const t = kind === 'home' ? 'home' : 'relay';
  return methods.find(m => m.provider === 'mondial_relay' && m.type === t);
}

/** Garde l’id existant si déjà en base (commandes), sinon id stable pour une nouvelle org. */
function resolveMrDirectMethodId(methods: ShippingMethodConfig[], kind: 'home' | 'relay'): string {
  const existing = findMrDirectMethod(methods, kind);
  if (existing?.id) return existing.id;
  return kind === 'home' ? MR_DIRECT_HOME_ID : MR_DIRECT_RELAY_ID;
}

interface MrOfferDraft {
  enabled: boolean;
  maxWeight: number;
  selectedCountries: string[];
  countryPrices: Record<string, number>;
}

function emptyMrOfferDraft(): MrOfferDraft {
  return {
    enabled: false,
    maxWeight: 30,
    selectedCountries: [],
    countryPrices: {},
  };
}

/** Catalogue Sendcloud pertinent selon le provider (plateforme vs compte marchand). */
function effectiveSendcloudCatalog(
  methodOrDraft: Pick<ShippingMethodConfig, 'provider'>,
  platformCatalog: SendCloudShippingMethod[],
  merchantCatalog: SendCloudShippingMethod[],
): SendCloudShippingMethod[] {
  return methodOrDraft.provider === 'sendcloud_api_key' ? merchantCatalog : platformCatalog;
}

function computeSendcloudPlatformPrice(
  method: ShippingMethodConfig,
  catalog: SendCloudShippingMethod[],
  countryCode: string,
): number {
  if (method.provider !== 'sendcloud' && method.provider !== 'sendcloud_api_key') return 0;
  const stored = method.platformCountryPrices?.[countryCode];
  if (stored != null && Number.isFinite(stored)) return stored;
  const pm =
    method.sendcloudMethodId != null
      ? catalog.find(c => c.id === method.sendcloudMethodId)
      : undefined;
  const row = pm?.countries?.find(
    c => sendcloudCountryCode(c as { code?: string; iso_2?: string }) === countryCode,
  );
  if (row == null) return 0;
  return method.provider === 'sendcloud_api_key' ? row.price : applySendcloudMarkup(row.price);
}

/** Pays supportés par Sendcloud pour cette méthode (catalogue uniquement). */
function getSendcloudSupportedCountryRows(
  method: ShippingMethodConfig,
  catalog: SendCloudShippingMethod[],
): { code: string; name: string }[] {
  const pm =
    method.sendcloudMethodId != null
      ? catalog.find(c => c.id === method.sendcloudMethodId)
      : undefined;
  const out: { code: string; name: string }[] = [];
  const seen = new Set<string>();
  for (const c of pm?.countries ?? []) {
    const code = sendcloudCountryCode(c as { code?: string; iso_2?: string });
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push({
      code,
      name: COUNTRY_NAMES[code] ?? (c as { name?: string }).name ?? code,
    });
  }
  return out;
}

function buildExpandedDraftFromMethod(
  m: ShippingMethodConfig,
  platformCatalog: SendCloudShippingMethod[],
  merchantCatalog: SendCloudShippingMethod[],
): ShippingMethodConfig {
  if (m.provider === 'mondial_relay') {
    const mrRows = getMondialRelayCountryRows(m);
    const mrSet = new Set(mrRows.map(r => r.code));
    let available = m.availableCountries.filter(c => mrSet.has(c));
    if (available.length === 0 && mrRows.length > 0) {
      available = mrRows.map(r => r.code);
    }
    const countryPrices: Record<string, number> = {};
    for (const row of mrRows) {
      countryPrices[row.code] = m.countryPrices[row.code] ?? 0;
    }
    return {
      ...m,
      availableCountries: available,
      countryPrices,
      platformCountryPrices: undefined,
    };
  }
  if (m.provider !== 'sendcloud' && m.provider !== 'sendcloud_api_key') {
    return {
      ...m,
      availableCountries: [...m.availableCountries],
      countryPrices: { ...m.countryPrices },
      platformCountryPrices: m.platformCountryPrices ? { ...m.platformCountryPrices } : undefined,
    };
  }
  const catalog = effectiveSendcloudCatalog(m, platformCatalog, merchantCatalog);
  const supportedRows = getSendcloudSupportedCountryRows(m, catalog);
  if (supportedRows.length === 0) {
    return {
      ...m,
      availableCountries: [...m.availableCountries],
      countryPrices: { ...m.countryPrices },
      platformCountryPrices: m.platformCountryPrices ? { ...m.platformCountryPrices } : undefined,
    };
  }
  const supportedSet = new Set(supportedRows.map(r => r.code));
  let available = m.availableCountries.filter(c => supportedSet.has(c));
  if (available.length === 0) {
    available = supportedRows.map(r => r.code);
  }
  /** Prix pour tous les pays supportés Sendcloud (y compris non sélectionnés), pour ne pas perdre les tarifs au décochage. */
  const countryPrices: Record<string, number> = {};
  const platformCountryPrices: Record<string, number> = {};
  for (const row of supportedRows) {
    const code = row.code;
    const plat = computeSendcloudPlatformPrice(m, catalog, code);
    countryPrices[code] = m.countryPrices[code] ?? plat;
    platformCountryPrices[code] = m.platformCountryPrices?.[code] ?? plat;
  }
  return {
    ...m,
    availableCountries: available,
    countryPrices,
    platformCountryPrices,
  };
}

interface ShippingMethodsConfigProps {
  organizationId: string;
  onRefresh?: () => void;
}

const ShippingMethodsConfig: React.FC<ShippingMethodsConfigProps> = ({ organizationId, onRefresh }) => {
  const { t } = useTranslation();
  const { configuration } = useOrgaConfigurationContext();
  const canUseSendcloudPlatform = isSendcloudPlatformProviderEnabled(organizationId);

  const resolvedOrganizationId = configuration?.organizationId || organizationId;

  // ── Shared helper ────────────────────────────────────────────────────────
  const currentConfig = useCallback((): ShippingConfiguration => ({
    methods: [],
    ...configuration?.shippingConfig,
  }), [configuration?.shippingConfig]);

  const saveConfig = useCallback(async (updates: Partial<ShippingConfiguration>) => {
    await OrgaConfigurationRepository.update(resolvedOrganizationId, {
      shippingConfig: { ...currentConfig(), ...updates },
    });
    onRefresh?.();
  }, [resolvedOrganizationId, currentConfig, onRefresh]);

  // ── Shipping methods ─────────────────────────────────────────────────────
  const [shippingMethods, setShippingMethods] = useState<ShippingMethodConfig[]>([]);
  const [expandedMethod, setExpandedMethod] = useState<string | null>(null);
  const [sendcloudCatalog, setSendcloudCatalog] = useState<SendCloudShippingMethod[]>([]);
  const [sendcloudMerchantCatalog, setSendcloudMerchantCatalog] = useState<SendCloudShippingMethod[]>([]);
  /** Brouillon édition inline (vue développée — remplace l’ancienne modale Modifier) */
  const [expandedDetailDraft, setExpandedDetailDraft] = useState<ShippingMethodConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    ShippingService.browseSendcloudMethods()
      .then(list => {
        if (!cancelled) setSendcloudCatalog(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSendcloudCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hasKeys = configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey === true;
    if (!organizationId || !hasKeys) {
      setSendcloudMerchantCatalog([]);
      return () => {
        cancelled = true;
      };
    }
    ShippingService.browseSendcloudMethodsMerchant(organizationId)
      .then(list => {
        if (!cancelled) setSendcloudMerchantCatalog(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSendcloudMerchantCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey]);

  useEffect(() => {
    if (!expandedMethod) {
      setExpandedDetailDraft(null);
      return;
    }
    const m = shippingMethods.find(x => x.id === expandedMethod);
    if (m) {
      setExpandedDetailDraft(prev => {
        const next = buildExpandedDraftFromMethod(m, sendcloudCatalog, sendcloudMerchantCatalog);
        if (prev?.id === m.id) {
          return {
            ...next,
            customName: prev.customName,
            availableCountries: prev.availableCountries,
            countryPrices: { ...next.countryPrices, ...prev.countryPrices },
            platformCountryPrices: {
              ...(next.platformCountryPrices ?? {}),
              ...(prev.platformCountryPrices ?? {}),
            },
          };
        }
        return next;
      });
    } else {
      setExpandedDetailDraft(null);
    }
  }, [expandedMethod, shippingMethods, sendcloudCatalog, sendcloudMerchantCatalog]);
  const [savingMethods, setSavingMethods] = useState(false);

  useEffect(() => {
    const methods = configuration?.shippingConfig?.methods;
    if (Array.isArray(methods)) setShippingMethods(methods);
  }, [configuration?.shippingConfig?.methods]);

  const persistMethods = async (methods: ShippingMethodConfig[]) => {
    setSavingMethods(true);
    try {
      await saveConfig({ methods });
      setShippingMethods(methods);
    } finally {
      setSavingMethods(false);
    }
  };

  const addMethod = async (method: ShippingMethodConfig) => {
    await persistMethods([...shippingMethods, method]);
    toast.success('Méthode de livraison ajoutée');
  };

  const ensureMondialRelayDirectMethod = async (kind: 'home' | 'relay') => {
    if (!mondialRelayEnabled) {
      toast.error('Activez Mondial Relay dans Transporteurs');
      return;
    }
    if (!canUseMr) {
      toast.error('Renseignez d’abord les identifiants Mondial Relay (section Transporteurs)');
      return;
    }
    const existing = findMrDirectMethod(shippingMethods, kind);
    if (existing) {
      setExpandedMethod(existing.id);
      return;
    }
    const id = resolveMrDirectMethodId(shippingMethods, kind);
    const rows = getMondialRelayCountryRowsForNewMethod(kind === 'home' ? 'home' : 'relay');
    const defaultCountries = rows.map(r => r.code);
    const newMethod: ShippingMethodConfig = {
      id,
      provider: 'mondial_relay',
      carrier: 'mondial_relay',
      type: kind === 'home' ? 'home' : 'relay',
      mondialRelayModeLiv: kind === 'home' ? '24L' : '24R',
      weightRange: { min: 0, max: 30 },
      availableCountries: defaultCountries.length ? defaultCountries : ['FR'],
      countryPrices: Object.fromEntries(
        (defaultCountries.length ? defaultCountries : ['FR']).map((c) => [c, 0]),
      ),
      isActive: true,
    };
    await persistMethods([...shippingMethods, newMethod]);
    setExpandedMethod(id);
    toast.success(`Méthode Mondial Relay ${kind === 'home' ? 'domicile' : 'point relais'} ajoutée`);
  };

  const toggleMethodActive = async (id: string, isActive: boolean) => {
    const updated = shippingMethods.map(m => m.id === id ? { ...m, isActive } : m);
    await persistMethods(updated);
  };

  const deleteMethod = async (id: string) => {
    await persistMethods(shippingMethods.filter(m => m.id !== id));
    toast.success('Méthode supprimée');
  };

  // ── Modal Sendcloud uniquement (MR direct = bloc dédié dans Méthodes) ─────────
  const [addSendcloudModalOpen, setAddSendcloudModalOpen] = useState(false);
  const openAddSendcloudModal = () => setAddSendcloudModalOpen(true);
  const closeAddSendcloudModal = () => setAddSendcloudModalOpen(false);

  const [addSendcloudMerchantModalOpen, setAddSendcloudMerchantModalOpen] = useState(false);
  const [addMethodChooserOpen, setAddMethodChooserOpen] = useState(false);
  const [sendcloudKeyPublic, setSendcloudKeyPublic] = useState('');
  const [sendcloudKeySecret, setSendcloudKeySecret] = useState('');
  const [savingSendcloudKeys, setSavingSendcloudKeys] = useState(false);
  const [testingSendcloudKeys, setTestingSendcloudKeys] = useState(false);
  const [sendcloudKeysTestFeedback, setSendcloudKeysTestFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [sendcloudKeysValidatedFingerprint, setSendcloudKeysValidatedFingerprint] = useState<string | null>(null);

  const sendcloudKeysFingerprint = useMemo(() => {
    const pub = sendcloudKeyPublic.trim();
    const sec = sendcloudKeySecret.trim();
    return pub && sec ? `${pub}|${sec}` : null;
  }, [sendcloudKeyPublic, sendcloudKeySecret]);

  const canSaveSendcloudKeys =
    sendcloudKeysFingerprint != null &&
    sendcloudKeysValidatedFingerprint === sendcloudKeysFingerprint;

  const resetSendcloudKeysValidation = () => {
    setSendcloudKeysTestFeedback(null);
    setSendcloudKeysValidatedFingerprint(null);
  };

  const testSendcloudMerchantKeys = async () => {
    const hasFormKeys = Boolean(sendcloudKeyPublic.trim() && sendcloudKeySecret.trim());
    const hasSavedKeys = configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey === true;
    if (!hasFormKeys && !hasSavedKeys) {
      setSendcloudKeysTestFeedback({
        type: 'error',
        message: t('admin.configuration.shipping.sendcloudApiKeys.testMissingKeys'),
      });
      return;
    }
    setTestingSendcloudKeys(true);
    setSendcloudKeysTestFeedback(null);
    try {
      const r = await ShippingService.testSendcloudApiKeys({
        organizationId: resolvedOrganizationId,
        ...(hasFormKeys
          ? { publicKey: sendcloudKeyPublic.trim(), secretKey: sendcloudKeySecret.trim() }
          : {}),
      });
      if (!r.success) {
        setSendcloudKeysTestFeedback({
          type: 'error',
          message: r.error || t('admin.configuration.shipping.sendcloudApiKeys.testFailed'),
        });
        return;
      }
      if (hasFormKeys) {
        setSendcloudKeysValidatedFingerprint(
          `${sendcloudKeyPublic.trim()}|${sendcloudKeySecret.trim()}`,
        );
      }
      setSendcloudKeysTestFeedback({
        type: 'success',
        message: r.message || t('admin.configuration.shipping.sendcloudApiKeys.testSuccess'),
      });
    } finally {
      setTestingSendcloudKeys(false);
    }
  };

  const saveSendcloudMerchantKeys = async () => {
    if (!sendcloudKeyPublic.trim() || !sendcloudKeySecret.trim()) {
      toast.error(t('admin.configuration.shipping.sendcloudApiKeys.testMissingKeys'));
      return;
    }
    if (!canSaveSendcloudKeys) {
      toast.error(t('admin.configuration.shipping.sendcloudApiKeys.saveRequiresTest'));
      return;
    }
    setSavingSendcloudKeys(true);
    try {
      const r = await ShippingService.saveSendcloudMerchantKeys({
        organizationId: resolvedOrganizationId,
        publicKey: sendcloudKeyPublic.trim(),
        secretKey: sendcloudKeySecret.trim(),
      });
      if (!r.success) {
        const errMsg = r.error || t('admin.configuration.shipping.sendcloudApiKeys.testFailed');
        toast.error(errMsg);
        setSendcloudKeysValidatedFingerprint(null);
        setSendcloudKeysTestFeedback({ type: 'error', message: errMsg });
        return;
      }
      setSendcloudKeyPublic('');
      setSendcloudKeySecret('');
      setSendcloudKeysValidatedFingerprint(null);
      setSendcloudKeysTestFeedback(null);
      toast.success(t('admin.configuration.shipping.sendcloudApiKeys.saveSuccess'));
      onRefresh?.();
    } finally {
      setSavingSendcloudKeys(false);
    }
  };

  // ── Delete confirm ───────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean; methodId: string; methodName: string;
  }>({ open: false, methodId: '', methodName: '' });

  // ── Mondial Relay : deux offres fixes (domicile / point relais) ──────────
  const [mrOfferHome, setMrOfferHome] = useState<MrOfferDraft>(emptyMrOfferDraft);
  const [mrOfferRelay, setMrOfferRelay] = useState<MrOfferDraft>(emptyMrOfferDraft);
  const [mrOfferHomeExpanded, setMrOfferHomeExpanded] = useState(false);
  const [mrOfferRelayExpanded, setMrOfferRelayExpanded] = useState(false);
  const [savingMrOffers, setSavingMrOffers] = useState(false);

  /** Ne resynchronise les brouillons MR que si les méthodes MR en base changent (pas à chaque toggle Sendcloud). */
  const mrMethodsSyncKey = useMemo(() => {
    const home = findMrDirectMethod(shippingMethods, 'home');
    const relay = findMrDirectMethod(shippingMethods, 'relay');
    const sig = (m: ShippingMethodConfig | undefined) =>
      m
        ? `${m.id}|${m.isActive}|${(m.availableCountries ?? []).join(',')}|${JSON.stringify(m.countryPrices)}|${m.weightRange?.max ?? ''}`
        : '';
    return `${sig(home)}#${sig(relay)}`;
  }, [shippingMethods]);

  useEffect(() => {
    const home = findMrDirectMethod(shippingMethods, 'home');
    if (home) {
      const rows = getMondialRelayCountryRows(home);
      const rowSet = new Set(rows.map(r => r.code));
      setMrOfferHome({
        enabled: home.isActive,
        maxWeight: home.weightRange?.max ?? 30,
        selectedCountries: (home.availableCountries ?? []).filter(c => rowSet.has(c)),
        countryPrices: { ...home.countryPrices },
      });
    } else {
      setMrOfferHome(emptyMrOfferDraft());
    }
    const relay = findMrDirectMethod(shippingMethods, 'relay');
    if (relay) {
      const rows = getMondialRelayCountryRows(relay);
      const rowSet = new Set(rows.map(r => r.code));
      setMrOfferRelay({
        enabled: relay.isActive,
        maxWeight: relay.weightRange?.max ?? 30,
        selectedCountries: (relay.availableCountries ?? []).filter(c => rowSet.has(c)),
        countryPrices: { ...relay.countryPrices },
      });
    } else {
      setMrOfferRelay(emptyMrOfferDraft());
    }
  }, [mrMethodsSyncKey]);

  // NOTE: on autorise désormais l'édition inline (expand) pour Mondial Relay aussi.

  const saveMrOffers = async () => {
    if (!canUseMr) {
      toast.error('Renseignez d’abord les identifiants Mondial Relay (section Transporteurs)');
      return;
    }
    const check = (label: string, draft: MrOfferDraft) => {
      if (draft.enabled && draft.selectedCountries.length === 0) {
        toast.error(`${label} : sélectionnez au moins un pays pour activer l’offre.`);
        return false;
      }
      return true;
    };
    if (!check('Livraison à domicile', mrOfferHome) || !check('Point relais', mrOfferRelay)) return;

    const build = (kind: 'home' | 'relay', draft: MrOfferDraft): ShippingMethodConfig | null => {
      if (!draft.enabled && draft.selectedCountries.length === 0) return null;
      const allowedRows = getMondialRelayCountryRowsForNewMethod(kind === 'home' ? 'home' : 'relay');
      const allowed = new Set(allowedRows.map(r => r.code));
      const selected = draft.selectedCountries.filter(c => allowed.has(c));
      const countryPrices: Record<string, number> = {};
      for (const code of allowedRows.map(r => r.code)) {
        countryPrices[code] = draft.countryPrices[code] ?? 0;
      }
      const id = resolveMrDirectMethodId(shippingMethods, kind);
      return {
        id,
        provider: 'mondial_relay',
        carrier: 'mondial_relay',
        type: kind === 'home' ? 'home' : 'relay',
        weightRange: { min: 0, max: Math.min(999, Math.max(0.1, draft.maxWeight)) },
        availableCountries: selected,
        countryPrices,
        isActive: draft.enabled,
        mondialRelayModeLiv: kind === 'home' ? '24L' : '24R',
      };
    };

    setSavingMrOffers(true);
    try {
      const withoutMr = shippingMethods.filter(m => m.provider !== 'mondial_relay');
      const h = build('home', mrOfferHome);
      const r = build('relay', mrOfferRelay);
      const next = [...withoutMr, ...(h ? [h] : []), ...(r ? [r] : [])];
      await persistMethods(next);
      toast.success('Offres Mondial Relay enregistrées');
    } finally {
      setSavingMrOffers(false);
    }
  };

  // ── Providers ────────────────────────────────────────────────────────────
  const [sendcloudEnabled, setSendcloudEnabled] = useState(true);
  const [mondialRelayEnabled, setMondialRelayEnabled] = useState(false);
  const [expandedProvider, setExpandedProvider] = useState<'sendcloud' | 'mondial_relay' | null>(null);
  const [mrCreds, setMrCreds] = useState({
    enseigne: '',
    customerId: '',
    apiLogin: '',
    apiPassword: '',
  });
  const [savingMrCreds, setSavingMrCreds] = useState(false);
  const [mrSetupHelpExpanded, setMrSetupHelpExpanded] = useState(false);

  useEffect(() => {
    const cfg = configuration?.shippingConfig;
    if (cfg) {
      setSendcloudEnabled(cfg.sendcloudEnabled !== false);
      setMondialRelayEnabled(cfg.mondialRelayEnabled ?? false);
      if (cfg.mondialRelayConfig) {
        const m = cfg.mondialRelayConfig;
        setMrCreds({
          enseigne: m.enseigne ?? '',
          customerId: m.customerId ?? '',
          apiLogin: m.apiLogin ?? '',
          // Ne jamais réinjecter un mot de passe depuis Firestore / le réseau
          apiPassword: '',
        });
      }
    }
  }, [configuration?.shippingConfig]);

  const toggleProvider = async (provider: 'sendcloud' | 'mondial_relay', enabled: boolean) => {
    if (provider === 'sendcloud') {
      setSendcloudEnabled(enabled);
      await saveConfig({ sendcloudEnabled: enabled });
        } else {
      setMondialRelayEnabled(enabled);
      await saveConfig({ mondialRelayEnabled: enabled });
      if (enabled) setExpandedProvider('mondial_relay');
    }
  };

  const saveMrCredentials = async () => {
    if (!mrCreds.enseigne.trim() || !mrCreds.apiLogin.trim()) {
      toast.error('Renseignez le code enseigne (CustomerId) et l’email API');
      return;
    }
    const mr = configuration?.shippingConfig?.mondialRelayConfig;
    const alreadyHasPassword =
      mr?.hasApiPassword === true ||
      !!mr?.apiPasswordMasked ||
      !!mr?.apiPassword;
    if (!alreadyHasPassword && !mrCreds.apiPassword.trim()) {
      toast.error('Renseignez le mot de passe API Connect pour la première configuration');
      return;
    }
    setSavingMrCreds(true);
    try {
      const result = await ShippingService.saveMondialRelayMerchantCredentials({
        organizationId: resolvedOrganizationId,
        enseigne: mrCreds.enseigne.trim(),
        apiLogin: mrCreds.apiLogin.trim(),
        customerId: mrCreds.customerId.trim() || undefined,
        apiPassword: mrCreds.apiPassword.trim() || undefined,
      });
      if (!result.success) {
        toast.error(result.error || 'Échec de l’enregistrement');
        return;
      }
      setMrCreds(p => ({ ...p, apiPassword: '' }));
      await saveConfig({ mondialRelayEnabled: true });
      setMondialRelayEnabled(true);
      toast.success('Configuration Mondial Relay sauvegardée (mot de passe sécurisé côté serveur)');
      onRefresh?.();
    } finally {
      setSavingMrCreds(false);
    }
  };

  // ── Sender address ───────────────────────────────────────────────────────
  const [senderConfig, setSenderConfig] = useState({
    name: '', street: '', houseNumber: '', city: '', postalCode: '', country: 'FR',
    companyName: '', email: '', phone: '',
  });
  const [isSavingSenderConfig, setIsSavingSenderConfig] = useState(false);
  const [senderAddressSearchValue, setSenderAddressSearchValue] = useState('');
  const [senderConfigLoaded, setSenderConfigLoaded] = useState(false);

  useEffect(() => {
    const sa = configuration?.shippingConfig?.senderAddress;
    if (sa) {
      setSenderConfig({
        name: sa.name ?? '',
        street: sa.address_line_1 ?? '',
        houseNumber: sa.house_number ?? '',
        city: sa.city ?? '',
        postalCode: sa.postal_code ?? '',
        country: sa.country_code ?? 'FR',
        companyName: sa.company_name ?? '',
        email: sa.email ?? '',
        phone: sa.phone_number ?? '',
      });
      setSenderConfigLoaded(true);
    }
  }, [configuration]);

  useEffect(() => {
    if (senderConfigLoaded && senderConfig.street) {
      setSenderAddressSearchValue(`${senderConfig.street} ${senderConfig.city}`);
    }
  }, [senderConfigLoaded]);

  const handleSenderAddressSelect = (_: string, city: string, postalCode: string, country: string, houseNumber: string, street: string) => {
    setSenderConfig(p => ({ ...p, street, city, postalCode, country, houseNumber }));
  };

  const saveSenderConfig = async () => {
    const sa = senderConfig;
    if (!sa.name?.trim() || !sa.street?.trim() || !sa.postalCode?.trim() || !sa.city?.trim() || !sa.country?.trim()) {
      toast.error('Renseignez tous les champs obligatoires (nom, adresse, code postal, ville, pays)');
      return;
    }
    setIsSavingSenderConfig(true);
    try {
      await saveConfig({
          senderAddress: {
            name: sa.name.trim(),
          address_line_1: sa.street.trim(),
            house_number: sa.houseNumber?.trim() || undefined,
            postal_code: sa.postalCode.trim(),
            city: sa.city.trim(),
            country_code: sa.country.trim(),
            company_name: sa.companyName?.trim() || undefined,
            email: sa.email?.trim() || undefined,
            phone_number: sa.phone?.trim() || undefined,
        },
      });
      toast.success('Adresse expéditeur sauvegardée');
    } finally {
      setIsSavingSenderConfig(false);
    }
  };

  // ── Order merge config ───────────────────────────────────────────────────
  type OpenDaysRange = { start: number | null; end: number | null };
  const [orderMergeConfig, setOrderMergeConfig] = useState<{ mergeOpenDaysRanges: OpenDaysRange[] }>({
    mergeOpenDaysRanges: [],
  });
  const [isSavingMergeConfig, setIsSavingMergeConfig] = useState(false);

  useEffect(() => {
    const mc = configuration?.shippingConfig?.orderMergeConfig;
    if (mc) {
      setOrderMergeConfig({
        mergeOpenDaysRanges: mc.mergeOpenDaysRanges?.map(r => ({ start: r.start, end: r.end })) ?? [],
      });
    } else {
      setOrderMergeConfig({ mergeOpenDaysRanges: [] });
    }
  }, [configuration]);

  const getDaysInRange = (start: number, end: number): number[] => {
    if (start <= end) return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    return [...Array.from({ length: 7 - start }, (_, i) => start + i), ...Array.from({ length: end + 1 }, (_, i) => i)];
  };

  const getUsedDaysByOtherRanges = (idx: number): Set<number> => {
    const used = new Set<number>();
    orderMergeConfig.mergeOpenDaysRanges.forEach((r, i) => {
      if (i !== idx && r.start != null && r.end != null) {
        getDaysInRange(r.start, r.end).forEach(d => used.add(d));
      }
    });
    return used;
  };

  const rangesOverlap = (ranges: { start: number; end: number }[]): boolean => {
    for (let i = 0; i < ranges.length; i++) {
      const daysI = new Set(getDaysInRange(ranges[i].start, ranges[i].end));
      for (let j = i + 1; j < ranges.length; j++) {
        if (getDaysInRange(ranges[j].start, ranges[j].end).some(d => daysI.has(d))) return true;
      }
    }
    return false;
  };

  const saveMergeConfig = async () => {
    const completeRanges = orderMergeConfig.mergeOpenDaysRanges
      .filter(r => r.start != null && r.end != null)
      .map(r => ({ start: r.start!, end: r.end! }));
    
    if (completeRanges.some(r => r.start === r.end)) {
      toast.error(t('admin.configuration.shipping.orderMergeConfig.sameDayNotAllowed'));
      return;
    }
    if (rangesOverlap(completeRanges)) {
      toast.error(t('admin.configuration.shipping.orderMergeConfig.rangesOverlap'));
      return;
    }
    
    setIsSavingMergeConfig(true);
    try {
      await saveConfig({
          orderMergeConfig: {
            mergeOpenDaysRanges: completeRanges.length ? completeRanges : undefined,
            updatedAt: new Date().toISOString(),
        },
      });
      toast.success(t('admin.configuration.shipping.orderMergeConfig.saved'));
    } finally {
      setIsSavingMergeConfig(false);
    }
  };

  const addOpenDaysRange = () =>
    setOrderMergeConfig(c => ({ ...c, mergeOpenDaysRanges: [...c.mergeOpenDaysRanges, { start: null, end: null }] }));

  const removeOpenDaysRange = (idx: number) =>
    setOrderMergeConfig(c => ({ ...c, mergeOpenDaysRanges: c.mergeOpenDaysRanges.filter((_, i) => i !== idx) }));

  const setOpenDaysRange = (idx: number, field: 'start' | 'end', value: number | null) =>
    setOrderMergeConfig(c => ({
      ...c,
      mergeOpenDaysRanges: c.mergeOpenDaysRanges.map((r, i) => i === idx ? { ...r, [field]: value } : r),
    }));

  const getMergeConfigSummary = (): string => {
    const completeRanges = orderMergeConfig.mergeOpenDaysRanges.filter(r => r.start != null && r.end != null);
    if (completeRanges.length === 0) return t('admin.configuration.shipping.orderMergeConfig.summaryDefault');
    const descs = completeRanges.map(r =>
      `${t(`admin.configuration.shipping.orderMergeConfig.weekday${r.start}`)} → ${t(`admin.configuration.shipping.orderMergeConfig.weekday${r.end}`)}`
    );
    if (completeRanges.length === 1) return t('admin.configuration.shipping.orderMergeConfig.summaryOneRange', { range: descs[0] });
    return t('admin.configuration.shipping.orderMergeConfig.summaryMultipleRanges', { ranges: descs.join(', ') });
  };

  // ── Édition inline (expand) ─────────────────────────────────────────────
  const toggleExpandedDetailCountry = (methodId: string, code: string) => {
    setExpandedDetailDraft(d => {
      if (!d || d.id !== methodId) return d;
      const isIn = d.availableCountries.includes(code);
      if (isIn && d.availableCountries.length <= 1) {
        toast.error('Au moins un pays doit rester sélectionné');
        return d;
      }
      if (isIn) {
        return { ...d, availableCountries: d.availableCountries.filter(c => c !== code) };
      }
      if (d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key') {
        const cat = effectiveSendcloudCatalog(d, sendcloudCatalog, sendcloudMerchantCatalog);
        const supported = new Set(
          getSendcloudSupportedCountryRows(d, cat).map(r => r.code),
        );
        if (supported.size > 0 && !supported.has(code)) {
          return d;
        }
        const plat = computeSendcloudPlatformPrice(d, cat, code);
        return {
          ...d,
          availableCountries: [...d.availableCountries, code],
          countryPrices: { ...d.countryPrices, [code]: d.countryPrices[code] ?? plat },
          platformCountryPrices: {
            ...(d.platformCountryPrices ?? {}),
            [code]: d.platformCountryPrices?.[code] ?? plat,
          },
        };
      }
      if (d.provider === 'mondial_relay' && !getMondialRelayCountryCodes(d).includes(code)) {
        return d;
      }
      return {
        ...d,
        availableCountries: [...d.availableCountries, code],
        countryPrices: { ...d.countryPrices, [code]: d.countryPrices[code] ?? 0 },
      };
    });
  };

  const setExpandedDetailCountryPrice = (methodId: string, code: string, price: number) => {
    setExpandedDetailDraft(prev =>
      prev && prev.id === methodId ? { ...prev, countryPrices: { ...prev.countryPrices, [code]: price } } : prev,
    );
  };

  const commitExpandedDetailDraft = async () => {
    if (!expandedDetailDraft) return;
    const d = expandedDetailDraft;
    if (d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key') {
      const cat = effectiveSendcloudCatalog(d, sendcloudCatalog, sendcloudMerchantCatalog);
      const supportedRows = getSendcloudSupportedCountryRows(d, cat);
      const supportedSet = new Set(supportedRows.map(r => r.code));
      const allowed =
        supportedSet.size > 0
          ? d.availableCountries.filter(c => supportedSet.has(c))
          : d.availableCountries;
      if (allowed.length === 0) {
        toast.error('Sélectionnez au moins un pays supporté par Sendcloud');
        return;
      }
      const supportedCountryCodes = supportedRows.map(r => r.code);
      const platformCountryPrices: Record<string, number> = {};
      const countryPrices: Record<string, number> = {};
      const fillPrices = (codes: string[]) => {
        for (const c of codes) {
          const plat = computeSendcloudPlatformPrice(d, cat, c);
          platformCountryPrices[c] = d.platformCountryPrices?.[c] ?? plat;
          countryPrices[c] = d.countryPrices[c] ?? plat;
        }
      };
      if (supportedCountryCodes.length > 0) {
        fillPrices(supportedCountryCodes);
      } else {
        fillPrices(allowed);
      }
      const next: ShippingMethodConfig = {
        ...d,
        customName: d.customName?.trim() || undefined,
        weightRange: { min: d.weightRange?.min ?? 0, max: d.weightRange?.max ?? 30 },
        availableCountries: allowed,
        countryPrices,
        platformCountryPrices,
      };
      await persistMethods(shippingMethods.map(m => (m.id === d.id ? next : m)));
    } else if (d.provider === 'mondial_relay') {
      const mrCodes = getMondialRelayCountryCodes(d);
      const mrSet = new Set(mrCodes);
      const allowed = d.availableCountries.filter(c => mrSet.has(c));
      if (allowed.length === 0) {
        toast.error('Sélectionnez au moins un pays Mondial Relay');
        return;
      }
      const countryPrices: Record<string, number> = {};
      for (const c of mrCodes) {
        countryPrices[c] = d.countryPrices[c] ?? 0;
      }
      const next: ShippingMethodConfig = {
        ...d,
        customName: d.customName?.trim() || undefined,
        weightRange: { min: d.weightRange?.min ?? 0, max: d.weightRange?.max ?? 30 },
        availableCountries: allowed,
        countryPrices,
        carrier: 'mondial_relay',
      };
      delete next.platformCountryPrices;
      await persistMethods(shippingMethods.map(m => (m.id === d.id ? next : m)));
    } else {
      const countryPrices = d.availableCountries.reduce<Record<string, number>>(
        (acc, c) => ({ ...acc, [c]: d.countryPrices[c] ?? 0 }),
        {},
      );
      const next: ShippingMethodConfig = {
        ...d,
        customName: d.customName?.trim() || undefined,
        weightRange: { min: d.weightRange?.min ?? 0, max: d.weightRange?.max ?? 30 },
        availableCountries: d.availableCountries,
        countryPrices,
      };
      delete next.platformCountryPrices;
      await persistMethods(shippingMethods.map(m => (m.id === d.id ? next : m)));
    }
    toast.success('Méthode mise à jour');
  };

  const selectAllExpandedCountries = (methodId: string) => {
    setExpandedDetailDraft(d => {
      if (!d || d.id !== methodId) return d;
      if (d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key') {
        const cat = effectiveSendcloudCatalog(d, sendcloudCatalog, sendcloudMerchantCatalog);
        const pm = cat.find(c => c.id === d.sendcloudMethodId);
        const codes =
          pm?.countries
            ?.map(c => sendcloudCountryCode(c as { code?: string; iso_2?: string }))
            .filter(Boolean) ?? [];
        if (codes.length === 0) return d;
        const countryPrices = { ...d.countryPrices };
        const platformCountryPrices = { ...(d.platformCountryPrices ?? {}) };
        for (const code of codes) {
          const plat = computeSendcloudPlatformPrice(d, cat, code);
          platformCountryPrices[code] = platformCountryPrices[code] ?? plat;
          countryPrices[code] = countryPrices[code] ?? plat;
        }
        return {
          ...d,
          availableCountries: Array.from(new Set(codes)),
          countryPrices,
          platformCountryPrices,
        };
      }
      if (d.provider === 'mondial_relay') {
        const codes = [...getMondialRelayCountryCodes(d)];
        const countryPrices = { ...d.countryPrices };
        for (const code of codes) {
          countryPrices[code] = countryPrices[code] ?? 0;
        }
        return { ...d, availableCountries: codes, countryPrices };
      }
      const codes = COUNTRY_LIST.map(({ code }) => code);
      const countryPrices = { ...d.countryPrices };
      for (const code of codes) {
        countryPrices[code] = countryPrices[code] ?? 0;
      }
      return { ...d, availableCountries: codes, countryPrices };
    });
  };

  const keepOneExpandedCountry = (methodId: string) => {
    setExpandedDetailDraft(d => {
      if (!d || d.id !== methodId || d.availableCountries.length === 0) return d;
      const keep = d.availableCountries[0];
      /** Ne pas tronquer countryPrices : garder les tarifs des pays décochés. */
        return {
        ...d,
        availableCountries: [keep],
      };
    });
  };

  const alreadyAddedSendcloudIds = shippingMethods
    .filter(m => m.provider === 'sendcloud' && m.sendcloudMethodId)
    .map(m => m.sendcloudMethodId!);
  const alreadyAddedSendcloudMerchantIds = shippingMethods
    .filter(m => m.provider === 'sendcloud_api_key' && m.sendcloudMethodId)
    .map(m => m.sendcloudMethodId!);

  const hasMrCredentials = !!(
    configuration?.shippingConfig?.mondialRelayConfig?.enseigne &&
    configuration?.shippingConfig?.mondialRelayConfig?.apiLogin &&
    (
      configuration?.shippingConfig?.mondialRelayConfig?.hasApiPassword === true ||
      !!configuration?.shippingConfig?.mondialRelayConfig?.apiPasswordMasked ||
      !!configuration?.shippingConfig?.mondialRelayConfig?.apiPassword
    )
  );
  const canUseMr = mondialRelayEnabled && hasMrCredentials;
  const visibleMethods = shippingMethods.filter((m) => (
    m.provider === 'sendcloud'
      ? sendcloudEnabled && canUseSendcloudPlatform
      : (
    m.provider === 'mondial_relay'
      ? mondialRelayEnabled
      : sendcloudEnabled
      )
  ));

  // ── Render ───────────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState<'methods' | 'settings'>('methods');

  const tabs = [
    { id: 'methods' as const, label: 'Méthodes de livraison', icon: Truck },
    { id: 'settings' as const, label: 'Paramètres', icon: Settings },
  ];

  return (
    <div className="w-full space-y-6 min-h-[600px]">
      {/* Header */}
        <div>
        <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight">
          {t('admin.configuration.shipping.sectionTitle')}
        </h2>
        <p className="text-xs sm:text-sm text-foreground-secondary mt-0.5">
          {t('admin.configuration.shipping.sectionDescription')}
        </p>
        </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 p-1 bg-background-tertiary rounded-xl w-full sm:w-fit">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-surface text-foreground shadow-sm'
                  : 'text-foreground-secondary hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'settings' && (
      <>{/* ── Fusion commandes ────────────────────────────────────────────── */}
      <Card variant="default">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
              <GitMerge className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.shipping.orderMergeConfig.title')}</h3>
              <p className="text-xs text-foreground-secondary mt-0.5">{t('admin.configuration.shipping.orderMergeConfig.description')}</p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-background-tertiary/50 border border-border">
              <p className="text-sm text-foreground">{getMergeConfigSummary()}</p>
            </div>
            <div className="p-4 rounded-lg bg-background-tertiary/30 border border-border">
              <p className="text-sm font-medium text-foreground mb-3">{t('admin.configuration.shipping.orderMergeConfig.openDaysLabel')}</p>
              <div className="space-y-2">
                {orderMergeConfig.mergeOpenDaysRanges.map((range, idx) => {
                  const usedByOthers = getUsedDaysByOtherRanges(idx);
                  return (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border border-border bg-surface">
                    <select
                      value={range.start ?? ''}
                        onChange={e => setOpenDaysRange(idx, 'start', e.target.value === '' ? null : Number(e.target.value))}
                      className="px-2 py-1.5 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface"
                    >
                      <option value="">{t('admin.configuration.shipping.orderMergeConfig.openDaysFrom')}</option>
                        {[0, 1, 2, 3, 4, 5, 6].map(day => {
                        const sameDay = range.end != null && day === range.end;
                          const wouldOverlap = range.end != null && getDaysInRange(day, range.end).some(d => usedByOthers.has(d));
                        return (
                            <option key={day} value={day} disabled={usedByOthers.has(day) || sameDay || wouldOverlap}>
                          {t(`admin.configuration.shipping.orderMergeConfig.weekday${day}`)}
                        </option>
                        );
                      })}
                    </select>
                    <span className="text-foreground-secondary">→</span>
                    <select
                      value={range.end ?? ''}
                        onChange={e => setOpenDaysRange(idx, 'end', e.target.value === '' ? null : Number(e.target.value))}
                      className="px-2 py-1.5 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface"
                    >
                      <option value="">{t('admin.configuration.shipping.orderMergeConfig.openDaysTo')}</option>
                        {[0, 1, 2, 3, 4, 5, 6].map(day => {
                        const sameDay = range.start != null && day === range.start;
                          const wouldOverlap = range.start != null && getDaysInRange(range.start, day).some(d => usedByOthers.has(d));
                        return (
                            <option key={day} value={day} disabled={usedByOthers.has(day) || sameDay || wouldOverlap}>
                          {t(`admin.configuration.shipping.orderMergeConfig.weekday${day}`)}
                        </option>
                        );
                      })}
                    </select>
                      <button type="button" onClick={() => removeOpenDaysRange(idx)} className="p-1 rounded text-foreground-secondary hover:bg-error/10 hover:text-error ml-auto">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  );
                })}
                <Button variant="ghost" size="sm" onClick={addOpenDaysRange} leftIcon={<Plus className="w-3 h-3" />} className="text-primary">
                  {t('admin.configuration.shipping.orderMergeConfig.addRange')}
                </Button>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="primary" size="md" onClick={saveMergeConfig} disabled={isSavingMergeConfig} isLoading={isSavingMergeConfig}>
                {t('admin.configuration.shipping.orderMergeConfig.save')}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Adresse expéditeur ──────────────────────────────────────────── */}
      <Card variant="default">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Adresse expéditeur</h3>
              <p className="text-xs text-foreground-secondary mt-0.5">Adresse d&apos;expédition requise pour les API d&apos;envoi (Sendcloud v3, Mondial Relay).</p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Nom du contact *', field: 'name' as const, placeholder: 'Marie Doe', type: 'text' },
                { label: "Nom de l'entreprise", field: 'companyName' as const, placeholder: 'Mon entreprise', type: 'text' },
                { label: 'Email', field: 'email' as const, placeholder: 'contact@exemple.com', type: 'email' },
                { label: 'Téléphone', field: 'phone' as const, placeholder: '+33612345678', type: 'tel' },
              ].map(({ label, field, placeholder, type }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
                <input
                    type={type}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface"
                    value={(senderConfig as any)[field] ?? ''}
                    onChange={e => setSenderConfig(p => ({ ...p, [field]: e.target.value }))}
                    placeholder={placeholder}
                />
              </div>
              ))}
              </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Adresse de l&apos;entrepôt *</label>
              <GooglePlacesSearch
                onAddressSelect={(address, city, postalCode, country, houseNumber, street) => {
                  handleSenderAddressSelect(address, city, postalCode, country, houseNumber, street);
                  setSenderAddressSearchValue(address);
                }}
                placeholder="Rechercher une adresse..."
                value={senderAddressSearchValue}
                onChange={setSenderAddressSearchValue}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface"
              />
              {senderConfig.street && (
                <div className="mt-2 p-3 bg-background-tertiary rounded text-sm text-foreground-secondary">
                  <p className="font-medium text-foreground">{senderConfig.street} {senderConfig.houseNumber}</p>
                  <p>{senderConfig.postalCode} {senderConfig.city}</p>
                  <p>{senderConfig.country}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-4">
              <Button variant="primary" size="md" onClick={saveSenderConfig} disabled={isSavingSenderConfig} isLoading={isSavingSenderConfig} className="w-full sm:w-auto">
                Sauvegarder l&apos;adresse expéditeur
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      </>)}

      {activeTab === 'methods' && (<>
      {/* ── Providers actifs ─────────────────────────────────────────────── */}
      <Card variant="default">
        <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-primary" />
              </div>
              <div>
              <h3 className="text-lg font-semibold text-foreground">Transporteurs actifs</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">
                Activez Sendcloud et/ou Mondial Relay, puis le comportement des colis au moment du paiement
                </p>
              </div>
          </div>
        </CardHeader>
        <CardBody className="!p-0">
          {/* Comportement commun aux providers (Sendcloud + Mondial Relay) */}
          <div className="px-4 py-3.5 border-b border-border bg-background-tertiary/30">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
                  <Package className="w-4 h-4 text-primary" />
            </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">Génération auto des colis au paiement</p>
                  <p className="text-xs text-foreground-secondary mt-1 leading-relaxed">
                    Après paiement (Stripe API Key / PayPal) : création immédiate des étiquettes si possible.
                    Sendcloud plateforme : débit crédits CashLive. Sendcloud avec vos clés API : facturation Sendcloud chez vous, sans crédit CashLive.
                    Mondial Relay direct : votre contrat MR, sans crédit CashLive.
                    Désactivé : tout passe par l’admin (lot ou fiche commande), utile pour ajuster le poids MR avant envoi.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 sm:pl-2">
                <span className="text-xs font-medium text-foreground-secondary whitespace-nowrap">
                  {configuration?.shippingConfig?.autoParcelGenerationOnPayment !== false ? 'Activée' : 'Manuelle'}
                </span>
                <label
                  htmlFor="shipping-auto-parcel-on-payment"
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    id="shipping-auto-parcel-on-payment"
                    checked={configuration?.shippingConfig?.autoParcelGenerationOnPayment !== false}
                    onChange={async e => {
                      await saveConfig({ autoParcelGenerationOnPayment: e.target.checked });
                      toast.success(
                        e.target.checked
                          ? 'Génération auto au paiement activée'
                          : 'Mode manuel : créer les colis depuis l’admin',
                      );
                    }}
                  />
                  <span className="sr-only">Basculer génération automatique des colis au paiement</span>
                  <div
                    className={`w-9 h-5 rounded-full transition-colors relative ${
                      configuration?.shippingConfig?.autoParcelGenerationOnPayment !== false
                        ? 'bg-success'
                        : 'bg-background-secondary border border-border'
                    }`}
                  >
                    <div
                      className={`w-3.5 h-3.5 bg-white rounded-full shadow absolute top-[3px] transition-transform ${
                        configuration?.shippingConfig?.autoParcelGenerationOnPayment !== false
                          ? 'translate-x-[18px]'
                          : 'translate-x-[3px]'
                      }`}
                    />
                        </div>
                </label>
                          </div>
            </div>
          </div>
          {/* ── Sendcloud row ── */}
          <div className="border-b border-border last:border-0">
            <button
              type="button"
              onClick={() => setExpandedProvider(expandedProvider === 'sendcloud' ? null : 'sendcloud')}
              className="w-full flex items-center justify-between p-4 hover:bg-background-tertiary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/></svg>
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">Sendcloud</p>
                  <p className="text-xs text-foreground-secondary">Plateforme et/ou vos clés API</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {sendcloudEnabled && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-success-10 text-success-dark font-medium">Actif</span>
                )}
                <label className="relative inline-flex items-center cursor-pointer" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={sendcloudEnabled}
                    onChange={e => toggleProvider('sendcloud', e.target.checked)}
                  />
                  <div className={`w-9 h-5 rounded-full transition-colors ${sendcloudEnabled ? 'bg-success' : 'bg-background-secondary border border-border'}`}>
                    <div className={`w-3.5 h-3.5 bg-white rounded-full shadow absolute top-[3px] transition-transform ${sendcloudEnabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}></div>
                        </div>
                </label>
                {expandedProvider === 'sendcloud' ? <ChevronUp className="w-4 h-4 text-foreground-secondary" /> : <ChevronDown className="w-4 h-4 text-foreground-secondary" />}
              </div>
            </button>
            {expandedProvider === 'sendcloud' && (
              <div className="px-4 pb-4 pt-1 bg-background-tertiary/30 space-y-3">
                {canUseSendcloudPlatform && (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800">
                    <p className="font-medium mb-1">Sendcloud — contrat CashLive (plateforme)</p>
                    <p className="text-xs text-blue-600">
                      Catalogue et étiquettes via notre compte Sendcloud. Les frais d’étiquette passent par les crédits CashLive lorsque c’est le mode configuré.
                    </p>
                  </div>
                )}
                <div className="p-3 rounded-lg border border-border bg-surface text-sm space-y-2">
                  <p className="font-medium text-foreground">Sendcloud — votre compte (clés API)</p>
                  <p className="text-xs text-foreground-secondary">
                    Stockez vos clés pour ajouter des méthodes depuis votre propre compte Sendcloud. Aucune marge CashLive sur ces méthodes.
                  </p>
                  {configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey && (
                    <p className="text-xs text-success-dark">
                      Configuré : {configuration.shippingConfig.sendcloudApiKeyConfig.publicKeyMasked ?? '…'} /{' '}
                      {configuration.shippingConfig.sendcloudApiKeyConfig.secretKeyMasked ?? '…'}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      className="px-3 py-2 border border-border rounded-lg text-sm bg-surface text-foreground"
                      placeholder="Clé publique Sendcloud"
                      value={sendcloudKeyPublic}
                      onChange={e => {
                        setSendcloudKeyPublic(e.target.value);
                        resetSendcloudKeysValidation();
                      }}
                      autoComplete="off"
                    />
                    <input
                      type="password"
                      className="px-3 py-2 border border-border rounded-lg text-sm bg-surface text-foreground"
                      placeholder="Clé secrète Sendcloud"
                      value={sendcloudKeySecret}
                      onChange={e => {
                        setSendcloudKeySecret(e.target.value);
                        resetSendcloudKeysValidation();
                      }}
                      autoComplete="new-password"
                    />
                  </div>
                  {sendcloudKeysTestFeedback && (
                    <div
                      className={`rounded-lg border px-3 py-2 text-xs ${
                        sendcloudKeysTestFeedback.type === 'success'
                          ? 'border-success/30 bg-success-10 text-success-dark'
                          : 'border-error/30 bg-error-10 text-error'
                      }`}
                      role="alert"
                    >
                      {sendcloudKeysTestFeedback.message}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={testSendcloudMerchantKeys}
                      disabled={testingSendcloudKeys || savingSendcloudKeys}
                      isLoading={testingSendcloudKeys}
                      leftIcon={!testingSendcloudKeys ? <TestTube className="w-4 h-4" /> : undefined}
                    >
                      {t('admin.configuration.shipping.sendcloudApiKeys.testButton')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={saveSendcloudMerchantKeys}
                      disabled={savingSendcloudKeys || testingSendcloudKeys || !canSaveSendcloudKeys}
                      isLoading={savingSendcloudKeys}
                      title={
                        !canSaveSendcloudKeys
                          ? t('admin.configuration.shipping.sendcloudApiKeys.saveRequiresTest')
                          : undefined
                      }
                    >
                      {t('admin.configuration.shipping.sendcloudApiKeys.saveButton')}
                    </Button>
                  </div>
                  {!canSaveSendcloudKeys && sendcloudKeysFingerprint && (
                    <p className="text-xs text-foreground-secondary">
                      {t('admin.configuration.shipping.sendcloudApiKeys.saveRequiresTest')}
                    </p>
                  )}
                </div>
                            </div>
                          )}
                          </div>

          {/* ── Mondial Relay row ── */}
          <div>
            <button
              type="button"
              onClick={() => setExpandedProvider(expandedProvider === 'mondial_relay' ? null : 'mondial_relay')}
              className="w-full flex items-center justify-between p-4 hover:bg-background-tertiary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">Mondial Relay</p>
                  <p className="text-xs text-foreground-secondary">
                    {t('admin.configuration.shipping.mondialRelay.directContract')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {canUseMr && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-success-10 text-success-dark font-medium">Actif</span>
                )}
                {mondialRelayEnabled && !hasMrCredentials && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-warning-10 text-warning-dark font-medium">Config requise</span>
                )}
                <label className="relative inline-flex items-center cursor-pointer" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={mondialRelayEnabled}
                    onChange={e => toggleProvider('mondial_relay', e.target.checked)}
                  />
                  <div className={`w-9 h-5 rounded-full transition-colors ${mondialRelayEnabled ? 'bg-success' : 'bg-background-secondary border border-border'}`}>
                    <div className={`w-3.5 h-3.5 bg-white rounded-full shadow absolute top-[3px] transition-transform ${mondialRelayEnabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}></div>
                  </div>
                </label>
                {expandedProvider === 'mondial_relay' ? <ChevronUp className="w-4 h-4 text-foreground-secondary" /> : <ChevronDown className="w-4 h-4 text-foreground-secondary" />}
              </div>
            </button>
            {expandedProvider === 'mondial_relay' && (
              <div className="px-4 pb-4 pt-1 bg-background-tertiary/30 space-y-4">
                <p className="text-xs text-foreground-secondary">
                  {t('admin.configuration.shipping.mondialRelay.apiV2Hint')}
                </p>

                <div className="rounded-lg border border-primary-20 bg-primary-5 overflow-hidden text-sm text-foreground">
                  <button
                    type="button"
                    onClick={() => setMrSetupHelpExpanded(prev => !prev)}
                    className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-primary-10/60 transition-colors"
                    aria-expanded={mrSetupHelpExpanded}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Info className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="font-semibold text-foreground text-sm">
                        {t('admin.configuration.shipping.mondialRelay.setupTitle')}
                      </span>
                      <span className="text-xs font-medium text-primary">
                        {mrSetupHelpExpanded
                          ? t('admin.configuration.shipping.mondialRelay.setupCollapse')
                          : t('admin.configuration.shipping.mondialRelay.setupExpand')}
                      </span>
                    </div>
                    {mrSetupHelpExpanded ? (
                      <ChevronUp className="w-4 h-4 text-foreground-secondary flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-foreground-secondary flex-shrink-0" />
                    )}
                  </button>
                  {mrSetupHelpExpanded && (
                    <div className="px-4 pb-4 pt-0 space-y-2 border-t border-primary-20">
                      <p className="text-xs text-foreground-secondary leading-relaxed pt-3">
                        {t('admin.configuration.shipping.mondialRelay.setupIntro')}
                      </p>
                      <a
                        href="https://connect.mondialrelay.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                      >
                        {t('admin.configuration.shipping.mondialRelay.setupConnectLink')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      <p className="text-xs text-foreground-secondary">
                        {t('admin.configuration.shipping.mondialRelay.setupApiPage')}
                      </p>
                      <p className="text-xs text-foreground-secondary">
                        {t('admin.configuration.shipping.mondialRelay.setupUrlPattern')}
                      </p>
                      <code className="block text-xs font-mono bg-surface border border-border rounded px-2 py-1.5 break-all text-foreground-secondary">
                        {t('admin.configuration.shipping.mondialRelay.setupUrlExample')}
                      </code>
                      <p className="text-xs font-medium text-foreground pt-1">
                        {t('admin.configuration.shipping.mondialRelay.mappingTitle')}
                      </p>
                      <ul className="text-xs text-foreground-secondary space-y-1 list-disc list-inside">
                        <li>{t('admin.configuration.shipping.mondialRelay.mapBrandId')}</li>
                        <li>{t('admin.configuration.shipping.mondialRelay.mapApiLogin')}</li>
                        <li>{t('admin.configuration.shipping.mondialRelay.mapApiPassword')}</li>
                      </ul>
                      <p className="text-xs text-foreground-secondary">
                        {t('admin.configuration.shipping.mondialRelay.prodVsTest')}
                      </p>
                      <p className="text-xs text-warning-dark">
                        {t('admin.configuration.shipping.mondialRelay.securityNote')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="mr-enseigne" className="block text-sm font-medium text-foreground mb-1">
                      Code enseigne / CustomerId *
                    </label>
                    <input
                      id="mr-enseigne"
                      type="text"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface font-mono text-sm"
                      value={mrCreds.enseigne}
                      onChange={e => setMrCreds(p => ({ ...p, enseigne: e.target.value }))}
                      placeholder={t('admin.configuration.shipping.mondialRelay.enseignePlaceholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="mr-customer-override" className="block text-sm font-medium text-foreground mb-1">
                      CustomerId (si différent du code enseigne)
                    </label>
                    <input
                      id="mr-customer-override"
                      type="text"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface font-mono text-sm"
                      value={mrCreds.customerId}
                      onChange={e => setMrCreds(p => ({ ...p, customerId: e.target.value }))}
                      placeholder="Optionnel"
                    />
                  </div>
                  <div>
                    <label htmlFor="mr-api-login" className="block text-sm font-medium text-foreground mb-1">
                      Identifiant API (email) *
                    </label>
                    <input
                      id="mr-api-login"
                      type="email"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface text-sm"
                      value={mrCreds.apiLogin}
                      onChange={e => setMrCreds(p => ({ ...p, apiLogin: e.target.value }))}
                      placeholder={t('admin.configuration.shipping.mondialRelay.apiLoginPlaceholder')}
                      autoComplete="username"
                    />
                  </div>
                  <div>
                    <label htmlFor="mr-api-password" className="block text-sm font-medium text-foreground mb-1">
                      Mot de passe API {hasMrCredentials ? '(optionnel si déjà enregistré)' : '*'}
                    </label>
                    <input
                      id="mr-api-password"
                      type="password"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface font-mono text-sm"
                      value={mrCreds.apiPassword}
                      onChange={e => setMrCreds(p => ({ ...p, apiPassword: e.target.value }))}
                      placeholder={hasMrCredentials ? 'Laisser vide pour conserver le secret actuel' : '••••••••••••'}
                      autoComplete="new-password"
                    />
                    {configuration?.shippingConfig?.mondialRelayConfig?.apiPasswordMasked && (
                      <p className="text-xs text-foreground-secondary mt-1 font-mono">
                        Secret actuel : {configuration.shippingConfig.mondialRelayConfig.apiPasswordMasked}
                      </p>
                    )}
                  </div>
                </div>
                {hasMrCredentials && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-success-10 border border-success-20">
                    <div className="w-2 h-2 rounded-full bg-success flex-shrink-0"></div>
                    <span className="text-sm text-success-dark font-medium">Identifiants configurés</span>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button variant="primary" size="sm" onClick={saveMrCredentials} disabled={savingMrCreds} isLoading={savingMrCreds}>
                    Sauvegarder les identifiants
                  </Button>
                          </div>
                        </div>
                          )}
                      </div>
        </CardBody>
      </Card>

      {/* ── Méthodes de livraison ────────────────────────────────────────── */}
      <Card variant="default">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Méthodes de livraison</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">
                  Mondial Relay : deux offres fixes. Sendcloud : catalogue plateforme et/ou votre compte (clés API).
                </p>
              </div>
            </div>
            <div className="flex w-full sm:w-auto">
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => setAddMethodChooserOpen(true)}
              className="w-full sm:w-auto"
            >
                Ajouter une méthode
            </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-8">
          {!canUseMr && mondialRelayEnabled && (
                <p className="text-sm text-amber-900 dark:text-amber-100 bg-amber-100/80 dark:bg-amber-950/40 rounded-lg px-3 py-2">
              Mondial Relay est activé, mais la configuration API est incomplète (voir Transporteurs).
            </p>
          )}

          {visibleMethods.length === 0 ? (
              <div className="text-center py-10 bg-background-tertiary rounded-2xl border-2 border-dashed border-border px-4">
                <Truck className="w-10 h-10 text-foreground-secondary mx-auto mb-3" />
                <p className="text-foreground-secondary text-sm">
                Aucune méthode configurée. Ajoutez une méthode Sendcloud (plateforme) ou configurez vos clés pour “Mon compte Sendcloud”.
                </p>
                        </div>
            ) : (
            <div className="space-y-3">
              {visibleMethods.map(method => {
                const isExpanded = expandedMethod === method.id;
                const carrierLabel =
                  method.provider === 'mondial_relay' ? 'Mondial Relay' : method.carrier;
                const label =
                  method.customName ||
                  `${carrierLabel} – ${method.type === 'home' ? 'Domicile' : 'Point relais'}`;

                return (
                  <div key={method.id} className="bg-background-tertiary rounded-xl border border-transparent hover:border-border transition-all">
                    <div className="flex items-center justify-between p-4 gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Active toggle */}
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={method.isActive}
                            onChange={e => toggleMethodActive(method.id, e.target.checked)}
                          />
                          <div className={`w-9 h-5 rounded-full transition-colors ${method.isActive ? 'bg-success' : 'bg-background-secondary border border-border'}`}>
                            <div className={`w-3.5 h-3.5 bg-white rounded-full shadow absolute top-[3px] transition-transform ${method.isActive ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}></div>
                      </div>
                        </label>

                        <div className="min-w-0">
                          <p className="font-semibold text-foreground text-sm truncate">{label}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                method.provider === 'mondial_relay'
                                  ? 'bg-orange-50 text-orange-700'
                                  : method.provider === 'sendcloud_api_key'
                                    ? 'bg-violet-50 text-violet-800'
                                    : 'bg-blue-50 text-blue-700'
                              }`}
                            >
                              {method.provider === 'mondial_relay'
                                ? 'Mondial Relay'
                                : method.provider === 'sendcloud_api_key'
                                  ? 'Sendcloud (vos clés)'
                                  : 'Sendcloud plateforme'}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${method.type === 'home' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'}`}>
                              {method.type === 'home' ? 'Domicile' : method.type === 'relay' ? 'Point relais' : 'Consigne'}
                              </span>
                            {method.weightRange && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-surface text-foreground-secondary">
                                {method.provider === 'sendcloud' || method.provider === 'sendcloud_api_key'
                                  ? `${formatSendcloudWeightKg(method.weightRange.min)}–${formatSendcloudWeightKg(method.weightRange.max)} kg`
                                  : `max ${method.weightRange.max} kg`}
                              </span>
                            )}
                            <span className="px-2 py-0.5 text-xs rounded-full bg-surface text-foreground-secondary">
                              {method.availableCountries.length} pays
                                </span>
                  </div>
            </div>
            </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setExpandedMethod(isExpanded ? null : method.id)}
                          className="p-2 hover:bg-surface rounded-lg transition-all text-foreground-secondary"
                          title={isExpanded ? 'Réduire' : 'Développer'}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, methodId: method.id, methodName: label })}
                          className="p-2 hover:bg-error-10 rounded-lg transition-all text-error-dark"
                          title="Supprimer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                </div>
                    </div>

                    {/* Édition inline (remplace la modale Modifier) */}
                    {isExpanded && expandedDetailDraft?.id === method.id && (
                      <div className="px-4 pb-4 pt-3 border-t border-border space-y-4">
                        {expandedDetailDraft.provider === 'sendcloud' ||
                        expandedDetailDraft.provider === 'sendcloud_api_key' ? (
                <div>
                            <p className="text-sm font-semibold text-foreground">
                              {formatSendcloudWeightKg(expandedDetailDraft.weightRange.min)}–{formatSendcloudWeightKg(expandedDetailDraft.weightRange.max)} kg
                            </p>
                  <p className="text-xs text-foreground-secondary mt-0.5">
                              Fourchette imposée par Sendcloud — non modifiable
                  </p>
                </div>
                        ) : (
                  <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Poids maximum (kg)</label>
                            <input
                              type="number"
                              min={0.1}
                              step={0.1}
                              className="w-32 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface"
                              value={expandedDetailDraft.weightRange?.max ?? 30}
                              onChange={e =>
                                setExpandedDetailDraft(prev =>
                                  prev && prev.id === method.id
                                    ? {
                                        ...prev,
                                        weightRange: {
                                          min: prev.weightRange?.min ?? 0,
                                          max: Number.parseFloat(e.target.value) || 30,
                                        },
                                      }
                                    : prev,
                                )
                              }
                            />
                      </div>
                    )}
                    <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Nom personnalisé</label>
                      <input
                        type="text"
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground bg-surface"
                            value={expandedDetailDraft.customName ?? ''}
                            onChange={e =>
                              setExpandedDetailDraft(d => (d && d.id === method.id ? { ...d, customName: e.target.value } : d))
                            }
                            placeholder="Nom affiché aux clients"
                      />
                    </div>
                    <div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <p className="text-sm font-medium text-foreground">
                              Configuration des pays de livraison
                              {(() => {
                                const d = expandedDetailDraft;
                                const n = d.availableCountries.length;
                                const sendcloudCat = effectiveSendcloudCatalog(d, sendcloudCatalog, sendcloudMerchantCatalog);
                                const sendcloudSupported =
                                  d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key'
                                    ? getSendcloudSupportedCountryRows(d, sendcloudCat)
                                    : [];
                                const mrSupported =
                                  d.provider === 'mondial_relay' ? getMondialRelayCountryRows(d) : [];
                                const total =
                                  (d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key') &&
                                  sendcloudSupported.length > 0
                                    ? sendcloudSupported.length
                                    : d.provider === 'mondial_relay' && mrSupported.length > 0
                                      ? mrSupported.length
                                      : COUNTRY_LIST.length;
                                return (
                                  <span className="text-foreground-secondary font-normal">
                                    {' '}
                                    ({n} pays sélectionné{n > 1 ? 's' : ''} sur {total} disponibles)
                                  </span>
                                );
                              })()}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="text-xs font-medium px-2.5 py-1 rounded-lg border border-border hover:bg-surface text-foreground"
                                onClick={() => selectAllExpandedCountries(method.id)}
                              >
                                Tout sélectionner
                              </button>
                              <button
                                type="button"
                                className="text-xs font-medium px-2.5 py-1 rounded-lg border border-border hover:bg-surface text-foreground"
                                onClick={() => keepOneExpandedCountry(method.id)}
                              >
                                Tout désélectionner
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                            {(() => {
                              const d = expandedDetailDraft;
                              const sendcloudCat = effectiveSendcloudCatalog(d, sendcloudCatalog, sendcloudMerchantCatalog);
                              const rows =
                                d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key'
                                  ? getSendcloudSupportedCountryRows(d, sendcloudCat)
                                  : d.provider === 'mondial_relay'
                                    ? getMondialRelayCountryRows(d)
                                    : COUNTRY_LIST;
                              if (
                                (d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key') &&
                                rows.length === 0
                              ) {
                                return (
                                  <p className="text-sm text-foreground-secondary col-span-full py-2">
                                    Chargement des pays Sendcloud pour cette méthode…
                                  </p>
                                );
                              }
                              return rows.map(({ code, name }) => {
                                const isSelected = d.availableCountries.includes(code);
                                const showRef =
                                  d.provider === 'sendcloud' || d.provider === 'sendcloud_api_key';
                                const plat = showRef
                                  ? computeSendcloudPlatformPrice(d, sendcloudCat, code)
                                  : 0;
                                return (
                                  <div
                                    key={code}
                                    className={`rounded-lg border p-2.5 transition-all ${isSelected ? 'border-success bg-success-10' : 'border-border bg-surface'}`}
                                  >
                                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleExpandedDetailCountry(method.id, code)}
                                        className="h-4 w-4 rounded text-success border-border flex-shrink-0"
                                      />
                                      <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
                                        {name}
                                      </span>
                                    </label>
                                    <div
                                      className={`mt-2 pl-7 space-y-1 ${isSelected ? '' : 'opacity-80'}`}
                                    >
                                      {showRef && (
                                        <p className="text-xs text-foreground-secondary">
                                          {d.provider === 'sendcloud_api_key'
                                            ? 'Réf. Sendcloud (votre compte)'
                                            : 'Tarif plateforme'}{' '}
                                          :{' '}
                                          {plat.toLocaleString('fr-FR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                          €
                                        </p>
                                      )}
                                      <div className="flex items-center gap-1">
                      <input
                                        type="number"
                                          min={0}
                                          step={0.01}
                                          value={d.countryPrices[code] ?? ''}
                                          placeholder={
                                            showRef
                                              ? plat.toLocaleString('fr-FR', {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                })
                                              : '0,00'
                                          }
                                          onChange={e =>
                                            setExpandedDetailCountryPrice(
                                              method.id,
                                              code,
                                              Number.parseFloat(e.target.value) || 0,
                                            )
                                          }
                                          onClick={e => e.preventDefault()}
                                          className="w-20 px-2 py-1 border border-border rounded text-sm text-foreground bg-surface"
                                        />
                                        <span className="text-xs text-foreground-secondary">€</span>
                    </div>
                  </div>
                </div>
                                );
                              });
                            })()}
                            </div>
                              </div>
                        <div className="flex justify-end pt-1">
                  <Button
                    variant="primary"
                    size="md"
                            onClick={commitExpandedDetailDraft}
                            disabled={savingMethods}
                            isLoading={savingMethods}
                          >
                            Enregistrer
                  </Button>
                </div>
              </div>
            )}
                  </div>
                );
              })}
          </div>
          )}
          </CardBody>
        </Card>
      </>)}

      <TailwindDialog
        isOpen={addMethodChooserOpen}
        onClose={() => setAddMethodChooserOpen(false)}
        title="Ajouter une méthode de livraison"
        maxWidth="sm"
        variant="themed"
      >
        <DialogContent className="p-5">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Choisissez un fournisseur</p>
              <p className="text-xs text-foreground-secondary mt-1">
                Sélectionnez le catalogue (Sendcloud) ou ajoutez directement une offre Mondial Relay.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {canUseSendcloudPlatform && (
              <button
                type="button"
                disabled={!sendcloudEnabled}
                onClick={() => {
                  setAddMethodChooserOpen(false);
                  openAddSendcloudModal();
                }}
                className={`w-full text-left rounded-xl border p-3 transition-all ${
                  sendcloudEnabled
                    ? 'bg-surface border-border hover:border-border-medium hover:bg-background-tertiary/40'
                    : 'bg-background-tertiary/30 border-border opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-4 h-4 text-blue-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">Sendcloud (catalogue plateforme)</p>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        Plateforme
                      </span>
                    </div>
                    <p className="text-xs text-foreground-secondary mt-1 leading-relaxed">
                      Méthodes issues du contrat CashLive. Les étiquettes peuvent utiliser les crédits CashLive.
                    </p>
                  </div>
                </div>
              </button>
              )}

              <button
                type="button"
                disabled={
                  !sendcloudEnabled ||
                  configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey !== true
                }
                onClick={() => {
                  setAddMethodChooserOpen(false);
                  setAddSendcloudMerchantModalOpen(true);
                }}
                className={`w-full text-left rounded-xl border p-3 transition-all ${
                  sendcloudEnabled && configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey === true
                    ? 'bg-surface border-border hover:border-border-medium hover:bg-background-tertiary/40'
                    : 'bg-background-tertiary/30 border-border opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <Package className="w-4 h-4 text-violet-800" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">Sendcloud (mon compte – clés API)</p>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-800">
                        Compte propre
                      </span>
                    </div>
                    <p className="text-xs text-foreground-secondary mt-1 leading-relaxed">
                      Méthodes et tarifs de votre compte Sendcloud. Pas de marge CashLive, pas de crédits CashLive.
                    </p>
                    {sendcloudEnabled && configuration?.shippingConfig?.sendcloudApiKeyConfig?.hasSecretKey !== true && (
                      <p className="text-xs text-amber-900 dark:text-amber-100 mt-2">
                        Configurez d’abord vos clés API (section Sendcloud).
                      </p>
                    )}
                  </div>
                </div>
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  disabled={!mondialRelayEnabled}
                  onClick={async () => {
                    setAddMethodChooserOpen(false);
                    await ensureMondialRelayDirectMethod('home');
                  }}
                  className={`w-full text-left rounded-xl border p-3 transition-all ${
                    mondialRelayEnabled
                      ? 'bg-surface border-border hover:border-border-medium hover:bg-background-tertiary/40'
                      : 'bg-background-tertiary/30 border-border opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-orange-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">Mondial Relay</p>
                      <p className="text-xs text-foreground-secondary mt-1">Domicile (contrat direct)</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  disabled={!mondialRelayEnabled}
                  onClick={async () => {
                    setAddMethodChooserOpen(false);
                    await ensureMondialRelayDirectMethod('relay');
                  }}
                  className={`w-full text-left rounded-xl border p-3 transition-all ${
                    mondialRelayEnabled
                      ? 'bg-surface border-border hover:border-border-medium hover:bg-background-tertiary/40'
                      : 'bg-background-tertiary/30 border-border opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-orange-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">Mondial Relay</p>
                      <p className="text-xs text-foreground-secondary mt-1">Point relais (contrat direct)</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {(!sendcloudEnabled || !mondialRelayEnabled) && (
              <div className="rounded-xl border border-border bg-background-tertiary/30 p-3">
                <p className="text-xs text-foreground-secondary leading-relaxed">
                  Certains choix sont désactivés. Activez les transporteurs dans la section “Transporteurs”.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions className="flex justify-end p-4 border-t border-border">
          <Button variant="ghost" size="md" onClick={() => setAddMethodChooserOpen(false)}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </TailwindDialog>

      {/* ── Modal : Sendcloud CarrierFinder ─────────────────────────────── */}
      <TailwindDialog
        isOpen={addSendcloudModalOpen}
        onClose={closeAddSendcloudModal}
        title="Sélectionner une méthode Sendcloud (plateforme)"
        maxWidth="7xl"
        variant="themed"
      >
        <DialogContent className="p-3 sm:p-5 min-h-0 flex-1 overflow-y-auto overflow-x-hidden max-h-[min(85vh,calc(90vh-5.5rem))] [scrollbar-gutter:stable]">
          <CarrierFinder
            catalogMode="platform"
            alreadyAddedIds={alreadyAddedSendcloudIds}
            onAddMethod={async method => {
              await addMethod(method);
              closeAddSendcloudModal();
            }}
          />
        </DialogContent>
        <DialogActions className="flex justify-end p-4 border-t border-border">
          <Button variant="ghost" size="md" onClick={closeAddSendcloudModal}>{t('common.cancel')}</Button>
        </DialogActions>
      </TailwindDialog>

      <TailwindDialog
        isOpen={addSendcloudMerchantModalOpen}
        onClose={() => setAddSendcloudMerchantModalOpen(false)}
        title="Sélectionner une méthode Sendcloud (votre compte)"
        maxWidth="7xl"
        variant="themed"
      >
        <DialogContent className="p-3 sm:p-5 min-h-0 flex-1 overflow-y-auto overflow-x-hidden max-h-[min(85vh,calc(90vh-5.5rem))] [scrollbar-gutter:stable]">
          <CarrierFinder
            catalogMode="merchant"
            organizationId={organizationId}
            alreadyAddedIds={alreadyAddedSendcloudMerchantIds}
            onAddMethod={async method => {
              await addMethod(method);
              setAddSendcloudMerchantModalOpen(false);
            }}
          />
        </DialogContent>
        <DialogActions className="flex justify-end p-4 border-t border-border">
          <Button variant="ghost" size="md" onClick={() => setAddSendcloudMerchantModalOpen(false)}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </TailwindDialog>

      {/* ── Modal : confirmer la suppression ────────────────────────────── */}
      <TailwindDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm(p => ({ ...p, open: false }))}
        title={t('admin.configuration.shipping.deleteConfirmTitle')}
        maxWidth="sm"
        variant="themed"
      >
        <DialogContent className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-error-10 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-error" />
            </div>
            <p className="text-foreground mb-2">{t('admin.configuration.shipping.deleteConfirmMessage')}</p>
            <p className="text-foreground font-semibold text-lg">{deleteConfirm.methodName}</p>
            <p className="text-foreground-secondary text-sm mt-3">{t('admin.configuration.shipping.deleteConfirmWarning')}</p>
          </div>
        </DialogContent>
        <DialogActions className="flex justify-end gap-3 p-4 border-t border-border bg-background-secondary">
          <Button variant="ghost" size="md" onClick={() => setDeleteConfirm(p => ({ ...p, open: false }))}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={async () => {
              await deleteMethod(deleteConfirm.methodId);
              setExpandedMethod(prev =>
                prev === deleteConfirm.methodId ? null : prev,
              );
              setDeleteConfirm(p => ({ ...p, open: false }));
            }}
            disabled={savingMethods}
            className="bg-error hover:bg-error-dark text-white"
          >
            {t('admin.configuration.shipping.deleteConfirmButton')}
          </Button>
        </DialogActions>
      </TailwindDialog>
    </div>
  );
};

export default ShippingMethodsConfig;
